import { isOptionFacet, isRangeFacet } from './typeHelpers';
function isAPIRedirectSearchResponse(response) {
    return 'redirect' in response;
}
export function transformResultVariation(variation, includeRaw = true) {
    const { url, image_url: imageUrl, group_ids: groupIds, description, facets, variation_id: variationId, ...otherMetadataFields } = variation.data;
    const transformedVariation = {
        itemName: variation.value,
        // Flatten the data object
        url,
        imageUrl,
        description,
        facets,
        variationId,
        rawResponse: includeRaw ? variation : undefined,
        // Remaining unmapped metadata fields
        data: otherMetadataFields,
    };
    return transformedVariation;
}
export function transformResultItem(item, includeRaw = true) {
    const { id: itemId, url, image_url: imageUrl, group_ids: groupIds, description, facets, groups, variation_id: variationId, ...otherMetadataFields } = item.data;
    const transformedItem = {
        matchedTerms: item.matched_terms,
        itemName: item.value,
        isSlotted: item.is_slotted,
        labels: item.labels,
        variations: item.variations?.map((variation) => transformResultVariation(variation)),
        variationsMap: item.variations_map,
        // Flatten the data object
        itemId,
        url,
        imageUrl,
        groupIds,
        groups,
        description,
        facets,
        variationId,
        rawResponse: includeRaw ? item : undefined,
        // Remaining unmapped metadata fields
        data: otherMetadataFields,
    };
    return transformedItem;
}
export function transformHierarchicalFacetOptions(facetOptions) {
    if (facetOptions.length === 0)
        return [];
    const transformedOptions = facetOptions.map((option) => {
        const { display_name: displayName, data, options, ...otherFields } = option;
        const { parent_value: parentValue, ...otherDataFields } = data;
        return {
            ...otherFields,
            data: {
                ...otherDataFields,
                parentValue,
            },
            displayName,
            options: transformHierarchicalFacetOptions(options),
        };
    });
    return transformedOptions;
}
export function transformResponseFacets(facets) {
    return facets.map((facet) => {
        const { display_name: displayName, name, type, data, hidden, min, max, status, options, } = facet;
        const transformedFacet = {
            displayName,
            name,
            type,
            data,
            hidden,
        };
        if (isRangeFacet(transformedFacet)) {
            transformedFacet.min = min;
            transformedFacet.max = max;
            transformedFacet.status = status;
        }
        if (isOptionFacet(transformedFacet)) {
            transformedFacet.options = options.map((option) => {
                const transformedFacetOption = {
                    status: option.status,
                    count: option.count,
                    displayName: option.display_name,
                    value: option.value,
                    data: option.data,
                };
                if (option.range) {
                    transformedFacetOption.range = option.range;
                }
                if (option.options) {
                    const { parent_value: parentValue, ...otherDataFields } = option.data;
                    transformedFacetOption.data = { ...otherDataFields, parentValue };
                    transformedFacetOption.options = transformHierarchicalFacetOptions(option.options);
                }
                return transformedFacetOption;
            });
        }
        return transformedFacet;
    });
}
export function transformResponseSortOptions(options) {
    if (options) {
        return options.map((option) => ({
            sortBy: option.sort_by,
            sortOrder: option.sort_order,
            displayName: option.display_name,
            status: option.status,
        }));
    }
    return [];
}
export function transformItemGroups(groups) {
    if (groups) {
        return groups.map((itemGroup) => ({
            groupId: itemGroup.group_id,
            displayName: itemGroup.display_name,
            count: itemGroup.count,
            data: itemGroup.data,
            children: transformItemGroups(itemGroup.children),
            parents: transformItemGroups(itemGroup.parents),
        }));
    }
    return [];
}
export function transformSearchResponse(res) {
    const { response, request, result_id: resultId } = res;
    if (!response || !request)
        return null;
    if (isAPIRedirectSearchResponse(response)) {
        return {
            resultId,
            request,
            redirect: response.redirect,
            rawApiResponse: res,
        }; // Type override due to partials in client-js
    }
    return {
        resultId,
        request,
        rawApiResponse: res,
        response: {
            totalNumResults: response.total_num_results,
            numResultsPerPage: request.num_results_per_page,
            results: response.results.map((result) => transformResultItem(result, false)),
            facets: transformResponseFacets(response.facets),
            groups: transformItemGroups(response.groups),
            sortOptions: transformResponseSortOptions(response.sort_options),
            refinedContent: response.refined_content,
        },
    }; // Type override due to partials in client-js
}
export function transformBrowseResponse(res) {
    const { response, request, result_id: resultId } = res;
    if (!response || !request)
        return null;
    return {
        resultId,
        request,
        rawApiResponse: res,
        response: {
            totalNumResults: response.total_num_results,
            numResultsPerPage: request.num_results_per_page,
            results: response.results.map((result) => transformResultItem(result, false)),
            facets: transformResponseFacets(response.facets),
            groups: transformItemGroups(response.groups),
            sortOptions: transformResponseSortOptions(response.sort_options),
            refinedContent: response.refined_content,
        },
    }; // Type override due to partials in client-js
}
